import React, { Children, lazy } from 'react';
import { motion } from 'framer-motion';
import { Star, CheckCircle2 } from 'lucide-react';
const TESTIMONIALS = [{
  id: 1,
  name: 'María Fernández',
  location: 'Miami, FL',
  photo: 'https://randomuser.me/api/portraits/women/44.jpg',
  headline: 'En menos de 2 semanas empecé a tomar decisiones que antes me daban miedo',
  quote: 'Siempre pensé que ganar más era cuestión de trabajar más horas. Este libro me abrió los ojos — el problema estaba en mi mente, no en mi esfuerzo. En menos de dos semanas empecé a tomar decisiones financieras que antes me daban miedo.',
  stars: 5
}, {
  id: 2,
  name: 'Carlos Ramírez',
  location: 'Houston, TX',
  photo: 'https://randomuser.me/api/portraits/men/32.jpg',
  headline: 'El Método 70/30 me dio el orden que ningún curso de finanzas me había dado antes',
  quote: 'Lo compré sin muchas expectativas por el precio tan bajo, pero la verdad me impactó. El paso 3 sobre el método 70/30 cambió completamente cómo manejo mi dinero. Ya no llego a fin de mes con ansiedad.',
  stars: 5
}, {
  id: 3,
  name: 'Ana Lucía Torres',
  location: 'Los Angeles, CA',
  photo: 'https://randomuser.me/api/portraits/women/68.jpg',
  headline: 'Por fin entendí que mi problema con el dinero no era mi culpa, era un programa heredado',
  quote: 'Lloré leyendo el primer capítulo porque describía exactamente lo que viví de niña con el dinero en mi casa. Entender de dónde venían mis bloqueos fue el primer paso para liberarme de ellos.',
  stars: 5
}, {
  id: 4,
  name: 'Roberto Mendoza',
  location: 'Chicago, IL',
  photo: 'https://randomuser.me/api/portraits/men/75.jpg',
  headline: 'Ser contador no me enseñó lo más importante: tener mentalidad de abundancia',
  quote: 'Soy contador y creía que sabía todo sobre dinero. Pero este libro me mostró que saber de números no es lo mismo que tener una mentalidad de abundancia. Totalmente recomendado.',
  stars: 5
}, {
  id: 5,
  name: 'Isabella Vargas',
  location: 'San Antonio, TX',
  photo: 'https://randomuser.me/api/portraits/women/90.jpg',
  headline: 'Dejamos de pelear por dinero y empezamos a construir un futuro juntos',
  quote: 'Mi esposo y yo lo leímos juntos y fue como terapia financiera para nuestra relación. Dejamos de pelear por dinero y empezamos a construir juntos. Vale muchísimo más de lo que cuesta.',
  stars: 5
}, {
  id: 6,
  name: 'Diego Herrera',
  location: 'Orlando, FL',
  photo: 'https://randomuser.me/api/portraits/men/45.jpg',
  headline: 'Lo que cursos de cientos de dólares no lograron, este ebook de $6 lo hizo',
  quote: 'Después de años intentando con cursos caros que no funcionaron, este ebook de $6 fue lo que realmente hizo click. Simple, directo y poderoso. Ya voy por la segunda lectura.',
  stars: 5
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function TestimonialsSection() {
  return <section className="py-12 md:py-16 px-4 md:px-8 bg-navy-midnight" data-id="element-303">
      <div className="max-w-6xl mx-auto" data-id="element-304">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="text-center mb-12 md:mb-16" data-id="element-305">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-headline font-extrabold text-white mb-6" data-id="element-306">
            Lo Que Dicen Quienes Ya Rompieron Su Código
          </h2>
          <p className="text-lg md:text-xl text-body font-body max-w-2xl mx-auto" data-id="element-307">
            Historias reales de personas que transformaron su relación con el
            dinero
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8" data-id="element-308">
          {TESTIMONIALS.map(testimonial => <motion.div key={testimonial.id} variants={itemVariants} className="bg-navy-dark rounded-xl border border-gold/20 p-6 md:p-8 flex flex-col shadow-soft hover:border-gold/40 transition-colors duration-300" data-id="element-309">
              {/* Stars */}
              <div className="flex gap-1 mb-4" data-id="element-310">
                {[...Array(testimonial.stars)].map((_, i) => <Star key={i} className="w-5 h-5 text-gold fill-gold" data-id="element-311" />)}
              </div>

              {/* Headline */}
              <h4 className="text-white font-headline font-extrabold text-xl mb-4" data-id="element-312">
                "{testimonial.headline}"
              </h4>

              {/* Quote */}
              <blockquote className="flex-1 text-body font-serif italic text-lg leading-relaxed mb-8" data-id="element-313">
                "{testimonial.quote}"
              </blockquote>

              {/* Divider */}
              <div className="w-full h-px bg-gradient-to-r from-gold/10 via-gold/30 to-transparent mb-6" data-id="element-314"></div>

              {/* Author Info */}
              <div className="flex items-center gap-4" data-id="element-315">
                <img src={testimonial.photo} alt={`Foto de ${testimonial.name}`} className="w-12 h-12 rounded-full border-2 border-gold/30 object-cover" loading="lazy" data-id="element-316" />
                <div data-id="element-317">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-318">
                    <h3 className="text-white font-headline font-bold" data-id="element-319">
                      {testimonial.name}
                    </h3>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded-full border border-[#10B981]/20" data-id="element-320">
                      <CheckCircle2 className="w-3 h-3" data-id="element-321" />
                      Compra verificada
                    </span>
                  </div>
                  <p className="text-body/70 text-sm font-body mt-0.5" data-id="element-322">
                    {testimonial.location}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}